import { UserManager, WebStorageStateStore, User, UserManagerSettings } from 'oidc-client-ts';

const normalizeBasePath = (value?: string | null): string => {
  if (!value || value === '/' || value === './') {
    return '';
  }
  const trimmed = value.replace(/^\/+|\/+$/g, '');
  return trimmed ? `/${trimmed}` : '';
};

const BASE_PATH = normalizeBasePath(import.meta.env.BASE_URL as string | undefined);

const buildAppUrl = (path: string): string => {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${BASE_PATH}${suffix}`;
};

const persistTokens = (user: User | null) => {
  if (!user) return;
  if (user.access_token) {
    localStorage.setItem('access_token', user.access_token);
  }
  if (user.refresh_token) {
    localStorage.setItem('refresh_token', user.refresh_token);
  }
};

const clearPersistedTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

const getOidcSettings = (): UserManagerSettings => {
  let keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL as string | undefined;
  const realm = (import.meta.env.VITE_KEYCLOAK_REALM as string) || 'optima';
  const clientId =
    (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string) ||
    (import.meta.env.VITE_OIDC_CLIENT_ID as string) ||
    'administration-frontend';

  if (!keycloakUrl) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      keycloakUrl = 'http://localhost:8080';
    } else {
      keycloakUrl = 'https://sso.pivotwise.nl';
    }
  }

  const redirectUri = buildAppUrl('/auth/callback');
  const postLogoutRedirectUri = buildAppUrl('/login');

  return {
    authority: `${keycloakUrl}/realms/${realm}`,
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    response_type: 'code',
    // Request standard profile info so user display/roles work consistently across apps
    scope: 'openid profile email roles',
    automaticSilentRenew: false,
    silent_redirect_uri: buildAppUrl('/auth/silent-renew'),
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Use localStorage for state to avoid sessionStorage clearing in dev containers/browsers
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: true,
    monitorSession: false,
    revokeTokensOnSignout: true,
    includeIdTokenInSilentRenew: false,
    filterProtocolClaims: true,
  };
};

class OidcAuthService {
  private userManager: UserManager;

  constructor() {
    this.userManager = new UserManager(getOidcSettings());

    this.userManager.events.addUserLoaded(user => {
      persistTokens(user);
    });

    this.userManager.events.addUserUnloaded(() => {
      clearPersistedTokens();
    });

    this.userManager.events.addAccessTokenExpired(() => {
      this.login(window.location.pathname + window.location.search).catch(() => this.logout());
    });
  }

  async login(returnUrl?: string): Promise<void> {
    const fallbackReturn = window.location.pathname + window.location.search;
    const targetReturn = returnUrl || fallbackReturn || (BASE_PATH || '/');
    const normalizedReturn = targetReturn.startsWith('/') ? targetReturn : `/${targetReturn}`;
    const state = normalizedReturn ? { returnUrl: normalizedReturn } : undefined;
    await this.userManager.signinRedirect({ state });
  }

  async handleCallback(): Promise<User> {
    const user = await this.userManager.signinRedirectCallback();
    persistTokens(user ?? null);
    const returnUrl =
      (user.state as any)?.returnUrl && typeof (user.state as any)?.returnUrl === 'string'
        ? (user.state as any).returnUrl
        : (BASE_PATH || '/');
    window.history.replaceState({}, document.title, returnUrl);
    return user;
  }

  async handleSilentCallback(): Promise<void> {
    await this.userManager.signinSilentCallback();
  }

  async logout(): Promise<void> {
    const user = await this.userManager.getUser();
    const settings = getOidcSettings();
    const idTokenHint = user?.id_token ? `&id_token_hint=${encodeURIComponent(user.id_token)}` : '';
    clearPersistedTokens();
    await this.userManager.removeUser();
    window.location.href =
      `${settings.authority}/protocol/openid-connect/logout?client_id=${settings.client_id}` +
      `&post_logout_redirect_uri=${encodeURIComponent(buildAppUrl('/login'))}${idTokenHint}`;
  }

  async getUser(): Promise<User | null> {
    try {
      return await this.userManager.getUser();
    } catch {
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!(user && !user.expired);
  }

  async refreshToken(): Promise<User | null> {
    try {
      const user = await this.userManager.signinSilent();
      persistTokens(user ?? null);
      return user;
    } catch (e) {
      return null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.access_token || localStorage.getItem('access_token') || null;
  }

  async getUserProfile(): Promise<any | null> {
    const user = await this.getUser();
    return user?.profile || null;
  }
}

const oidcAuth = new OidcAuthService();
export default oidcAuth;
